import { useState, useEffect } from 'react';
import { Box, Button, Typography, Link, Paper, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setTimeout(() => setShow(true), 2000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setShow(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            padding: '16px',
            pointerEvents: 'none',
          }}
        >
          <Paper
            sx={{
              p: 3,
              background: 'rgba(11, 15, 26, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              maxWidth: '600px',
              mx: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Cookie size={24} style={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  We use cookies
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                  By clicking "Accept All", you consent to our use of cookies.{' '}
                  <Link
                    href="/cookie-policy"
                    sx={{ color: '#a855f7', textDecoration: 'none' }}
                  >
                    Learn more
                  </Link>
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    onClick={handleAccept}
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                      },
                    }}
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={handleDecline}
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#a855f7',
                        background: 'rgba(168, 85, 247, 0.1)',
                      },
                    }}
                  >
                    Decline
                  </Button>
                </Box>
              </Box>
              <IconButton
                onClick={handleDecline}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#a855f7' },
                }}
              >
                <X size={18} />
              </IconButton>
            </Box>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
